import { useCallback, useMemo, useRef, useState } from 'react';

export type FilterPrimitiveType = 'string' | 'array' | 'dateISOString';

export type FilterDateFormat = 'date' | 'datetime';

export type FilterFieldMetadata = {
  key: string;
  label: string;
  type: FilterPrimitiveType;
  formatValue?: (value: string) => string;
  dateFormat?: FilterDateFormat;
  options?: Array<{ label: string; value: string }>;
};

export type FilterPillData = {
  fieldKey: string;
  label: string;
  value: string;
  displayValue: string;
};

const formatDateValue = (
  isoString: string,
  dateFormat?: FilterDateFormat,
): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;

    if (dateFormat === 'datetime') {
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return isoString;
  }
};

export const generateFilterPills = <T extends Record<string, any>>(
  values: T,
  metadata: Record<string, FilterFieldMetadata>,
): FilterPillData[] => {
  const pills: FilterPillData[] = [];

  Object.entries(values).forEach(([fieldKey, value]) => {
    const fieldMetadata = metadata[fieldKey];

    if (!fieldMetadata || !value) {
      return;
    }

    if (fieldMetadata.type === 'string' && typeof value === 'string') {
      let displayValue = value;

      if (fieldMetadata.formatValue) {
        displayValue = fieldMetadata.formatValue(value);
      } else if (fieldMetadata.options) {
        const option = fieldMetadata.options.find((opt) => opt.value === value);
        displayValue = option ? option.label : value;
      }

      pills.push({
        fieldKey,
        label: fieldMetadata.label,
        value,
        displayValue,
      });
    } else if (
      fieldMetadata.type === 'dateISOString' &&
      typeof value === 'string'
    ) {
      const displayValue = fieldMetadata.formatValue
        ? fieldMetadata.formatValue(value)
        : formatDateValue(value, fieldMetadata.dateFormat);

      pills.push({
        fieldKey,
        label: fieldMetadata.label,
        value,
        displayValue,
      });
    } else if (fieldMetadata.type === 'array' && Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          let displayValue = item;

          if (fieldMetadata.formatValue) {
            displayValue = fieldMetadata.formatValue(item);
          } else if (fieldMetadata.options) {
            const option = fieldMetadata.options.find(
              (opt) => opt.value === item,
            );
            displayValue = option ? option.label : item;
          }

          pills.push({
            fieldKey,
            label: fieldMetadata.label,
            value: item,
            displayValue,
          });
        }
      });
    }
  });

  return pills;
};

export const removeFilterValue = <T extends Record<string, any>>(
  currentValues: T,
  fieldKey: string,
  valueToRemove: string,
  metadata: Record<string, FilterFieldMetadata>,
): T => {
  const fieldMetadata = metadata[fieldKey];

  if (!fieldMetadata) {
    return currentValues;
  }

  const newValues = { ...currentValues } as any;

  if (
    fieldMetadata.type === 'string' ||
    fieldMetadata.type === 'dateISOString'
  ) {
    newValues[fieldKey] = null;
  } else if (fieldMetadata.type === 'array') {
    const currentArray = currentValues[fieldKey] as string[];
    newValues[fieldKey] = currentArray.filter((item) => item !== valueToRemove);
  }

  return newValues as T;
};

export type UseFilterConfig<T extends Record<string, any>> = {
  onFilterChange?: (values: T) => void;
};

export type UseFilterReturn<T extends Record<string, any>, R = unknown> = {
  filterRef: React.RefObject<R>;
  filterPills: FilterPillData[];
  isActiveFilter: boolean;
  filterValues: T | null;
  handleFilterChange: (params: { isActive: boolean; values: T }) => void;
  handleRemoveFilterPill: (fieldKey: string, value: string) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
};

export const useFilter = <T extends Record<string, any>, R = unknown>(
  config: UseFilterConfig<T> = {},
): UseFilterReturn<T, R> => {
  const { onFilterChange } = config;
  const filterRef = useRef<R>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<T | null>(null);

  const filterPills = useMemo(() => {
    if (!filterValues || !filterRef.current) return [];

    const ref = filterRef.current as any;
    if (!ref.getMetadata) return [];

    const metadata = ref.getMetadata();
    return generateFilterPills(filterValues, metadata);
  }, [filterValues]);

  const hasActiveFilters = useMemo(() => {
    return filterPills.length > 0;
  }, [filterPills]);

  const handleFilterChange = useCallback(
    ({ isActive, values }: { isActive: boolean; values: T }) => {
      setIsActiveFilter(isActive);
      setFilterValues(values);
      onFilterChange?.(values);
    },
    [onFilterChange],
  );

  const handleRemoveFilterPill = useCallback(
    (fieldKey: string, value: string) => {
      if (!filterValues || !filterRef.current) return;

      const ref = filterRef.current as any;
      if (!ref.getMetadata) return;

      const metadata = ref.getMetadata();
      const fieldMetadata = metadata[fieldKey];
      if (!fieldMetadata) return;

      const newValues = { ...filterValues } as Record<string, any>;

      if (
        fieldMetadata.type === 'string' ||
        fieldMetadata.type === 'dateISOString'
      ) {
        newValues[fieldKey] = null;
      } else if (fieldMetadata.type === 'array') {
        const currentArray = (filterValues as Record<string, any>)[
          fieldKey
        ] as string[];
        newValues[fieldKey] = currentArray.filter((item) => item !== value);
      }

      if (ref.updateValues) {
        ref.updateValues(newValues as T);
      }
    },
    [filterValues],
  );

  const clearAllFilters = useCallback(() => {
    if (!filterRef.current) return;

    const ref = filterRef.current as any;
    if (!ref.getMetadata || !ref.getValues) return;

    const metadata = ref.getMetadata();
    const currentValues = ref.getValues();
    const clearedValues = { ...currentValues } as Record<string, any>;

    Object.keys(metadata).forEach((key) => {
      const field = metadata[key];
      if (field.type === 'string' || field.type === 'dateISOString') {
        clearedValues[key] = null;
      } else if (field.type === 'array') {
        clearedValues[key] = [];
      }
    });

    if (ref.updateValues) {
      ref.updateValues(clearedValues as T);
    }
  }, []);

  return {
    filterRef,
    filterPills,
    isActiveFilter,
    filterValues,
    handleFilterChange,
    handleRemoveFilterPill,
    clearAllFilters,
    hasActiveFilters,
  };
};
